// Composition root. Wires the live data layer into the six-section layout, in
// priority order: lens → headline → count → money → geography+trend → markets.
import { AppShell } from './components/layout/AppShell.tsx';
import { Section } from './components/layout/Section.tsx';
import { ThemeToggle } from './components/controls/ThemeToggle.tsx';
import { RefreshControl } from './components/controls/RefreshControl.tsx';
import { LensNote } from './components/sections/LensNote.tsx';
import { HeadlineResult } from './components/sections/HeadlineResult.tsx';
import { CountDetail } from './components/sections/CountDetail.tsx';
import { MoneyPanel } from './components/sections/MoneyPanel.tsx';
import { GeographyTable } from './components/sections/GeographyTable.tsx';
import { MarginTrend } from './components/sections/MarginTrend.tsx';
import { MarketSentiment } from './components/sections/MarketSentiment.tsx';
// import { ManualEntry } from './components/sections/ManualEntry.tsx'; // hidden — CivicAPI is the live source
import { useTheme } from './hooks/useTheme.ts';
import { useLiveData } from './hooks/useLiveData.ts';
import { raceConfig } from './config/race.config.ts';
import { fmtDate } from './lib/time.ts';

export default function App() {
  const { mode, resolved, setTheme } = useTheme();
  const race = raceConfig;
  const live = useLiveData(race);
  const subtitle = [race.subtitle, fmtDate(race.electionDate)].filter(Boolean).join(' · ');

  const anyStale =
    Object.values(live.votes).some((s) => s.isStale) ||
    Object.values(live.markets).some((s) => s.isStale) ||
    live.finance.isStale;

  const primaryId = live.reconciliation.primary?.sourceId;
  const geoState = primaryId ? live.votes[primaryId] : undefined;

  return (
    <AppShell
      title={race.title}
      subtitle={subtitle}
      controls={
        <>
          <RefreshControl
            lastUpdatedAt={live.lastUpdatedAt}
            isRefreshing={live.isRefreshing}
            onRefresh={live.refreshNow}
            nowMs={live.nowMs}
            anyStale={anyStale}
          />
          <ThemeToggle mode={mode} onChange={setTheme} />
        </>
      }
      footer={
        <>
          <p className="muted">
            Provenance-first. Every figure carries its source and timestamp. Counts, money, and
            clearly-labeled market odds — no punditry.
          </p>
          <p className="muted dim">
            Vote counts are live from CivicAPI (a third-party aggregator, not an official source).
            Markets are odds, not counts. Money is live FEC independent expenditures, shown alongside
            reported figures.
          </p>
        </>
      }
    >
      {/* Consequential cards span both columns */}
      <Section id="headline" title="Result" kicker="Live count" span="full">
        <HeadlineResult
          race={race}
          reconciliation={live.reconciliation}
          voteStates={live.votes}
          resolved={resolved}
        />
      </Section>

      <Section id="count" title="Count detail" kicker="By candidate" span="full">
        <CountDetail
          race={race}
          reconciliation={live.reconciliation}
          voteStates={live.votes}
          resolved={resolved}
        />
        {/* <ManualEntry raceId={race.raceId} onApply={live.refreshNow} /> */}
      </Section>

      <Section id="money" title="Outside spending" kicker="Money" span="full">
        <MoneyPanel race={race} financeState={live.finance} resolved={resolved} />
      </Section>

      <Section id="geography" title="Geography & trend" kicker="Counties" span="full">
        <MarginTrend race={race} snapshots={live.snapshots} resolved={resolved} />
        <GeographyTable race={race} voteState={geoState} resolved={resolved} />
      </Section>

      {/* Softer cards pair up in the bottom two-column band */}
      <Section id="markets" title="Market sentiment" kicker="Odds, not counts" span="half">
        <MarketSentiment race={race} marketStates={live.markets} resolved={resolved} />
      </Section>

      <Section id="lens" title="What this race tests" kicker="Context" span="half">
        <LensNote note={race.lensNote} seatContext={race.seatContext} />
      </Section>
    </AppShell>
  );
}
